import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { Form, FormUsageContext } from 'components/Form/Form';
import {
  FormContextValue,
  FormSubmitFunction,
} from 'components/Form/FormContext';
import { BodySmall } from 'components/Text/BodySmall';
import { H1 } from 'components/Text/H1';
import { useTemporaryStorage } from 'hooks/useTemporaryStorage';
import { salariesFetchCareerPathSynonyms } from 'modules/salaries/api/salariesFetchCareerPathSynonyms';
import { salariesFetchReport } from 'modules/salaries/api/salariesFetchReport';
import { MainChartSidebarForm } from 'modules/salaries/components/MainChartSidebarForm/MainChartSidebarForm';
import { salarySurveyFaqRoute } from 'modules/salaries/routing/salaries.routes';
import { CalculatorReport } from 'modules/salaries/types/CalculatorReport';
import { EstimatedRange } from 'modules/salaries/types/EstimatedRange';
import { SalariesReportFormData } from 'modules/salaries/types/SalariesReportFormData';
import { colors } from 'theme/theme';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { getFirstParam } from 'utils/url/getFirstParam';

import {
  MainChartSidebarContainer,
  MainChartSidebarFormContainer,
  MainChartSidebarHeadingContainer,
} from './MainChartSidebar.styled';

type MainChartSidebarProps = {
  formAnalyticsTitle: string;
  formUsageContext: FormUsageContext;
  setInitialized: (initialized: boolean) => void;
  setLoading: (loading: boolean) => void;
  setCareerPathData: (synonyms: EstimatedRange[]) => void;
  setResults: (
    calculatorResult: CalculatorReport,
    values: SalariesReportFormData,
  ) => void;
  searchApiKey: string;
  setSearchApiKey: (arg0: string) => void;
};

type LocationState = {
  calculatorResult: CalculatorReport;
  initialValues: SalariesReportFormData;
};

export function MainChartSidebar({
  formAnalyticsTitle,
  formUsageContext,
  setLoading,
  setInitialized,
  setResults,
  setCareerPathData,
  searchApiKey,
  setSearchApiKey,
}: MainChartSidebarProps) {
  const location = useLocation<LocationState>();
  const navigate = useNavigate();

  const tmpIdFromParams = getFirstParam(location, 'tmpId');
  const [tmpId] = useState(tmpIdFromParams);

  const [initialValues, setInitialValues] = useState(
    location.state?.initialValues ? location.state.initialValues : {},
  );

  const [, temporaryStorage, temporaryStorageLoaded] = useTemporaryStorage(
    tmpId,
    true,
  );

  const getReport = useCallback(
    async (
      values: SalariesReportFormData,
      updateSubmitting?: FormContextValue<SalariesReportFormData>['updateSubmitting'],
    ) => {
      setLoading(true);

      const calculatorResult = await salariesFetchReport(values);

      const careerPath = await salariesFetchCareerPathSynonyms({
        ...values,
      });

      setResults(calculatorResult, values);
      setCareerPathData(careerPath.filter((career) => career.salary !== null));
      setLoading(false);

      if (updateSubmitting) {
        updateSubmitting(false);
      }
      setInitialized(true);
      const { pathname, search, hash } = location;
      navigate({ pathname, search, hash }, { state: {} });
    },
    [
      location,
      navigate,
      setLoading,
      setCareerPathData,
      setInitialized,
      setResults,
    ],
  );

  const onSubmit: FormSubmitFunction<SalariesReportFormData> = ({
    values,
    handleError,
    updateSubmitting,
  }) =>
    getReport(values, updateSubmitting).catch((e) => {
      updateSubmitting(e);
      setLoading(false);
      trackEvent('Error: Calculator Search');
      handleError(e);
    });

  useEffect(
    () => {
      if (
        tmpIdFromParams &&
        temporaryStorageLoaded &&
        temporaryStorage &&
        'surveyReport' in temporaryStorage
      ) {
        setInitialValues(temporaryStorage.surveyReport);
        setTimeout(() => {
          getReport(temporaryStorage.surveyReport);
        }, 0);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [temporaryStorage, tmpIdFromParams, temporaryStorageLoaded],
  );

  // Navigating to the page with initial values in the state
  // shows the report for those values
  useEffect(() => {
    if (location.state?.initialValues) {
      getReport(location.state.initialValues);
    }
  }, [getReport, location]);

  return (
    <MainChartSidebarContainer>
      <MainChartSidebarHeadingContainer>
        <H1 color={colors.white} styleAs="h2">
          {getText('Explore Salaries')}
        </H1>
      </MainChartSidebarHeadingContainer>
      <BodySmall
        lineHeight={1.625}
        color={colors.white}
        textAlign={['center', null, null, 'left']}
      >
        {getText('Learn what you could be earning in the nonprofit sector.')}{' '}
        <a
          href={salarySurveyFaqRoute.fullPath}
          target="_blank"
          style={{ color: colors.white }}
          rel="noreferrer"
        >
          {getText('See our FAQ for further information.')}
        </a>
      </BodySmall>
      <MainChartSidebarFormContainer>
        <Form
          analyticsTitle={formAnalyticsTitle}
          usageContext={formUsageContext}
          data-qa-id="main-chart-form"
          initialValues={initialValues}
          onSubmit={onSubmit}
          render={() => (
            <MainChartSidebarForm
              searchApiKey={searchApiKey}
              setSearchApiKey={setSearchApiKey}
            />
          )}
        />
      </MainChartSidebarFormContainer>
    </MainChartSidebarContainer>
  );
}
